/**
 * 检索结果 → 引用文本注入（T12）。
 *
 * 复用 `src/core/search/format` 产出的带编号引用文本，以**追加 system 角色 ChatMessage**
 * 的方式注入下一次 `ChatRequest.messages`；不修改 `src/core/types.ts` 契约。
 *
 * 注入的消息只进入请求上下文（不落库、不进入可见消息列表），因此不会污染对话历史。
 */

import { newId } from '../../core/ids';
import { formatSearchResults } from '../../core/search';
import type { SearchResult } from '../../core/search';
import type { ChatMessage } from '../../core/types';

/** 检索结果注入对话时使用的角色：system / context。 */
export const SEARCH_CONTEXT_ROLE = 'system' as const;

export interface SearchContextOptions {
  /** 单条摘要最大字符数，透传给 `formatSearchResults`。 */
  maxSnippetChars?: number;
  /** 自定义注入头；缺省时由核心层给出「以下为联网搜索结果…」。 */
  header?: string;
}

/** 生成注入用的引用文本；空结果返回空字符串（绝不产生只有表头的残骸）。 */
export function buildSearchContextContent(
  results: readonly SearchResult[],
  options: SearchContextOptions = {},
): string {
  return formatSearchResults(results, {
    includeHeader: true,
    ...(options.maxSnippetChars === undefined ? {} : { maxSnippetChars: options.maxSnippetChars }),
    ...(options.header === undefined ? {} : { header: options.header }),
  });
}

/**
 * 构造一条 system 引用上下文消息；空结果返回 null（调用方据此跳过注入）。
 *
 * 该消息是**运行期请求上下文**，不写入消息仓库。
 */
export function buildSearchContextMessage(
  results: readonly SearchResult[],
  conversationId: string,
  options: SearchContextOptions = {},
): ChatMessage | null {
  const content = buildSearchContextContent(results, options);
  if (content === '') return null;
  return {
    id: newId(),
    conversationId,
    role: SEARCH_CONTEXT_ROLE,
    content,
    createdAt: Date.now(),
    status: 'done',
  };
}

/** 追加引用上下文消息；空结果时原样返回一份浅拷贝（不注入空上下文）。 */
export function appendSearchContext(
  messages: readonly ChatMessage[],
  results: readonly SearchResult[],
  conversationId: string,
  options: SearchContextOptions = {},
): ChatMessage[] {
  const context = buildSearchContextMessage(results, conversationId, options);
  return context === null ? [...messages] : [...messages, context];
}
